/**
 * Hooks para Vendedores (SalesRep) — atribución de ventas y comisiones.
 * Un vendedor puede tener acceso al sistema (user vinculado, escenario 1) o ser
 * solo de atribución sin licencia (escenario 2).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export interface SalesRep {
  id: string
  name: string
  tax_id: string
  email: string
  phone: string
  commission_rate: string | null
  is_active: boolean
  user: string | null
  user_email: string
  has_system_access: boolean
  notes: string
  created_at: string
  updated_at: string
}

export type SalesRepInput = Partial<
  Pick<SalesRep, 'name' | 'tax_id' | 'email' | 'phone' | 'commission_rate' | 'is_active' | 'user' | 'notes'>
>

const salesRepKeys = {
  all: ['sales-reps'] as const,
  list: (params?: Record<string, string>) => ['sales-reps', 'list', params ?? {}] as const,
}

export function useSalesReps(params?: { active?: 'all' | 'true'; search?: string }) {
  return useQuery({
    queryKey: salesRepKeys.list(params as Record<string, string>),
    queryFn: async () => {
      const { data } = await apiClient.get<SalesRep[]>('/sales/sales-reps/', { params })
      return data
    },
    staleTime: 60 * 1000,
  })
}

export function useCreateSalesRep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SalesRepInput) => {
      const { data } = await apiClient.post<SalesRep>('/sales/sales-reps/', input)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesRepKeys.all })
      qc.invalidateQueries({ queryKey: ['users', 'sellers'] })
    },
  })
}

export function useUpdateSalesRep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: SalesRepInput & { id: string }) => {
      const { data } = await apiClient.patch<SalesRep>(`/sales/sales-reps/${id}/`, input)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesRepKeys.all })
      qc.invalidateQueries({ queryKey: ['users', 'sellers'] })
    },
  })
}

// ─── Matriz vendedor × producto ───────────────────────────────────────────────
// Nivel más específico de la cascada de comisión: gana sobre la comisión por
// defecto del vendedor y sobre la del producto. Solo administradores.

export interface CommissionRule {
  id: string
  sales_rep: string
  sales_rep_name: string
  product: string
  product_name: string
  product_sku: string
  product_is_commissionable: boolean
  commission_rate: string
  commission_base: 'total' | 'margin'
  commission_base_display: string
  margin_rate: string
  is_active: boolean
  notes: string
  created_at: string
  updated_at: string
}

export type CommissionRuleInput = Partial<
  Pick<CommissionRule, 'sales_rep' | 'product' | 'commission_rate' | 'commission_base' | 'margin_rate' | 'is_active' | 'notes'>
>

const commissionRuleKeys = {
  all: ['commission-rules'] as const,
  list: (params?: Record<string, string>) => ['commission-rules', 'list', params ?? {}] as const,
}

export function useCommissionRules(params?: { sales_rep?: string; product?: string }) {
  return useQuery({
    queryKey: commissionRuleKeys.list(params as Record<string, string>),
    queryFn: async () => {
      const { data } = await apiClient.get<{ results: CommissionRule[] }>(
        '/sales/commission-rules/', { params },
      )
      return data.results
    },
    staleTime: 60 * 1000,
  })
}

export function useCreateCommissionRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CommissionRuleInput) => {
      const { data } = await apiClient.post<CommissionRule>('/sales/commission-rules/', input)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionRuleKeys.all }),
  })
}

export function useUpdateCommissionRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: CommissionRuleInput & { id: string }) => {
      const { data } = await apiClient.patch<CommissionRule>(`/sales/commission-rules/${id}/`, input)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionRuleKeys.all }),
  })
}

export function useDeleteCommissionRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<{ detail: string }>(`/sales/commission-rules/${id}/`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionRuleKeys.all }),
  })
}
